import { Link } from 'react-router-dom';
import { Gavel } from 'lucide-react';
import { useActasHistorial } from './api';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/common/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { formatLongDate, formatShortDate } from '@/lib/format/date';

interface Props {
  condoId: string;
}

/**
 * Tabla (lista) de actas ordenadas desc por fecha de asamblea.
 * Muestra actas tanto firmadas como en borrador para que el admin
 * pueda navegar y completar la firma.
 */
export function HistorialActas({ condoId }: Props) {
  const { data, isLoading } = useActasHistorial(condoId);

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  const items = [...(data?.items ?? [])].sort(
    (a, b) =>
      new Date(b.asamblea.fechaProgramada).getTime() -
      new Date(a.asamblea.fechaProgramada).getTime(),
  );

  if (items.length === 0) {
    return (
      <EmptyState
        icon={<Gavel className="h-5 w-5" />}
        title="Sin actas"
        description="Las actas de asambleas cerradas apareceran aqui."
      />
    );
  }

  return (
    <ul className="divide-y divide-[var(--color-hairline)] border-y border-[var(--color-hairline)]">
      {items.map(({ asamblea, acta }) => (
        <li key={asamblea.id}>
          <Link
            to={`/app/condominios/${condoId}/asambleas/${asamblea.id}/acta`}
            className="flex items-center justify-between gap-4 px-2 py-4 transition-colors hover:bg-[var(--color-paper-dim)]"
          >
            <div className="min-w-0">
              <p className="font-display text-sm font-semibold capitalize">
                Asamblea {asamblea.tipo} — {formatLongDate(asamblea.fechaProgramada)}
              </p>
              <p className="mt-0.5 text-xs text-[var(--color-ink-muted)]">
                {asamblea.materias.length} materia(s) ·{' '}
                {acta.firmas.length > 0
                  ? `${acta.firmas.length} firma(s) · ultima: ${formatShortDate(
                      acta.firmas[acta.firmas.length - 1]!.firmadoEn,
                    )}`
                  : 'Sin firmas'}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Badge variant={acta.estado === 'firmada' ? 'success' : 'neutral'}>
                {acta.estado === 'firmada' ? 'Firmada' : 'Borrador'}
              </Badge>
            </div>
          </Link>
        </li>
      ))}
    </ul>
  );
}
